// Route Code Edited 09/29/2026
/**
 * Route — Inject the coverage quote into the Preferred Checkout cloned checkout button.
 *          2Modern (Snappy v3 / App Embed, plain Preferred Checkout base).
 *
 * GOAL
 *   Native plain-PC button text:      "ADD SHIPPING PROTECTION"
 *   Desired (visible AND announced):  "ADD SHIPPING PROTECTION + $XX.XX"
 *
 * CONTEXT
 *   The draft theme runs a plain Preferred Checkout widget (simulated via
 *   route-widget-config-override.js — no style_preset, so the button is a single-CTA
 *   button with no embedded quote). Plain PC renders the quote in the Value Prop
 *   Accordion header ONLY. This script mirrors the header's live quote into the cloned
 *   checkout button as a real, screen-reader-announced node, so the amount appears in
 *   BOTH places. The header wording ("Shipping Protection Included for $X.XX") is
 *   handled by the separate header-override script; this file only touches the button.
 *
 *   Employee Tools cannot place the quote in both header and button for this widget
 *   version, and widget code changes are out of scope, so this is a theme-side
 *   presentation override only. It READS Route's DOM and WRITES one <span> plus an
 *   aria-label update per cloned button. It never calls into Route and cannot affect
 *   the cart/checkout intercept flow.
 *
 * QUOTE SOURCE (read) vs TARGET (write)
 *   Read : .pw-coverage-description  (header; the CLASS, NOT the state-suffixed id —
 *          the id renders as pw-coverage-description-collapsed / -expanded, so an id
 *          selector matches nothing). Prefix and price are separate text nodes; a
 *          currency-amount regex on the element's textContent yields the price and is
 *          robust to the header-override script's prefix rewrite.
 *   Write: [data-route-cloned-button]  (documented, stable Route data attribute).
 *
 * WRAP-AS-ONE-LINE  (layout lives in CSS, not here)
 *   The visible desktop button is display:block, so the appended inline span flows and
 *   wraps with the CTA text automatically. An alternate layout (cart drawer / mobile) is
 *   display:flex / flex-wrap:nowrap; that button needs flex-wrap:wrap to let the amount
 *   wrap instead of overflowing. This is handled in CSS (see COMPANION CSS), NOT in this
 *   script — the script owns content + aria only.
 *
 * COMPANION CSS  (Route App Embed → Custom CSS field)
 *   [data-route-cloned-button] { flex-wrap: wrap !important; }
 *     No-op on the desktop block button; lets the flex (drawer/mobile) button wrap.
 *     !important reproduces the old inline-style behavior so a theme `flex-wrap: nowrap`
 *     rule can't win. Add `white-space: normal !important` on the same selector only if
 *     your theme forces nowrap on the button (it did not here).
 *
 * ACCESSIBILITY
 *   The cloned button carries an aria-label (= the CTA text). aria-label is the
 *   accessible name and OVERRIDES visible content for screen readers, so a visible-only
 *   span would NOT be announced. This script therefore also appends the amount to the
 *   aria-label. Route's clone sync (useButtonSynchronization) mirrors only
 *   class / disabled / style from the original — never aria-label — so this change is not
 *   reverted by Route; a full React remount resets it and the observer re-applies.
 *   NOTE: "+" is announced as "plus". ARIA_SEP below uses ", " for a cleaner spoken form
 *   ("...shipping protection, $XX.XX"). Set ARIA_SEP = SEPARATOR to make the announced
 *   string match the visible string exactly, if the merchant prefers.
 *
 * OBSERVERS / RE-SYNC
 *   - Body is watched for childList only (cheap) — this catches the cloned button
 *     appearing or remounting anywhere on the page.
 *   - characterData (the expensive one) is scoped to the cart regions only
 *     (CART_ROOT_SELECTORS), so in-place price-text edits are caught without firing on
 *     every text change across the whole page.
 *   - On a cart change Route re-quotes ASYNCHRONOUSLY; a single sync fired during the
 *     drawer re-render can read the OLD premium. A short trailing burst re-runs sync
 *     after activity settles. The trailing burst is armed ONLY on cart events (the
 *     async-re-quote trigger), not on every DOM mutation — that avoids timer churn and
 *     the burst being starved on a page that mutates continuously.
 *   - We cache the last good premium and never strip the amount on a transient empty read.
 *
 * IDEMPOTENCY / LOOP SAFETY
 *   Steady state is a no-op: the span text is only rewritten when it differs, and the
 *   aria-label only when it differs, so our own mutations don't re-trigger real work. We
 *   read the amount from the HEADER, never from the button, so the injected amount can't
 *   feed back into the read.
 *
 * SAFE DEGRADATION
 *   All work is wrapped in try/catch (per button as well as per pass). If a selector
 *   stops matching or the price can't be parsed, nothing is injected and the native
 *   button renders unchanged. Removing the <script> tag (and asset) fully reverts on
 *   reload.
 *
 * BFCACHE
 *   No pagehide teardown: a MutationObserver survives a back/forward-cache freeze and
 *   resumes on restore, so this script re-applies on its own without a pageshow handler.
 *   Widget re-init on restore (if any) is owned by snippets/bfcache-handler.liquid.
 *
 * KNOWN ASSUMPTIONS / FRAGILITY (Route/theme internals — may change without notice)
 *   - Header class:   .pw-coverage-description  (do NOT target the state-suffixed id)
 *   - Button anchor:  [data-route-cloned-button]
 *   - Cart roots:     #MainContent (cart page) + #CartDrawer (drawer) — both present at
 *     load and together covering every widget instance. If a theme mounts its drawer
 *     lazily, its scoped characterData is skipped until present; the body childList
 *     observer + cart events + trailing burst still cover re-quotes (graceful degrade).
 *   - Price is a "$" USD amount (store confirmed single-currency, USD, 09/22/2026).
 *     Broaden AMOUNT_SRC if Shopify Markets / multi-currency is ever enabled.
 *
 * DEBUG (opt-in, off by default)
 *   Enable via  window.__routePcButtonQuoteDebug = true  (before this script runs),
 *   or by adding  ?routeBtnQuoteDebug=1  to the URL. Debug only adds console output;
 *   it never changes what the script does to the page.
 *
 * INSTALLATION
 *   Upload as assets/route-pc-button-quote-override.js, then add near the bottom of
 *   layout/theme.liquid (immediately before </body>), AFTER the header-override script:
 *
 *   <!-- Route Code Edited 09/22/2026 -->
 *   <script src="{{ 'route-pc-button-quote-override.js' | asset_url }}" defer></script>
 *   <!-- End Route Code -->
 *
 *   Also add the COMPANION CSS above to the Route App Embed → Custom CSS field.
 */
(function routePcButtonQuote() {

  // Guard against accidental double-inclusion (two <script> references).
  if (window.__routePcButtonQuoteInit) return;
  window.__routePcButtonQuoteInit = true;

  // ─── Config (update if Route/theme renames internals, or the merchant changes wording) ──
  var HEADER_SELECTOR     = '.pw-coverage-description';   // quote source (class, NOT id)
  var BUTTON_SELECTOR     = '[data-route-cloned-button]'; // quote target (stable data attr)
  var CART_ROOT_SELECTORS = ['#MainContent', '#CartDrawer']; // characterData scope (cart only)
  var CART_EVENTS         = ['cart:change', 'cart:refresh', 'cart-drawer:refreshed', 'cart:updated'];
  var AMOUNT_SRC          = '[$£€]\\s?\\d[\\d,]*\\.\\d{2}'; // one source for every amount pattern
  var PRICE_RE            = new RegExp(AMOUNT_SRC);          // "$140.38"; broaden AMOUNT_SRC for multi-currency
  var ARIA_STRIP_RE       = new RegExp('\\s*[,+]\\s*' + AMOUNT_SRC + '\\s*$'); // trailing amount we appended
  var SEPARATOR           = ' + ';   // visible: "<CTA> + $XX.XX"
  var ARIA_SEP            = ', ';     // spoken:  "<CTA>, $XX.XX" ("+" reads as "plus")
  var QUOTE_ATTR          = 'data-route-quote'; // marks our injected span
  var ANNOUNCE            = true;     // also append the amount to aria-label
  var TRAILING_DELAYS     = [600, 1300]; // re-sync after cart activity settles (ms)
  var COALESCE_MS         = 16;
  // ────────────────────────────────────────────────────────────────────────────────────────

  var DEBUG = false;
  try {
    DEBUG = window.__routePcButtonQuoteDebug === true ||
            /[?&]routeBtnQuoteDebug=1(?:&|$)/.test(window.location.search);
  } catch (e) { /* leave DEBUG false */ }

  function warn() {
    if (!DEBUG) return;
    try {
      console.warn.apply(console, ['[RoutePcButtonQuote]'].concat(
        Array.prototype.slice.call(arguments)
      ));
    } catch (e) { /* never let logging throw */ }
  }

  var lastPremium = '';       // most recent valid price, kept across transient empty reads
  var unparsedWarned = false; // one-time debug latch for "header present, no price"

  // Read the current premium from the header. Returns "$140.38" or "".
  function readPremium() {
    var headers = document.querySelectorAll(HEADER_SELECTOR);
    if (!headers.length) return '';
    for (var i = 0; i < headers.length; i++) {
      var m = (headers[i].textContent || '').match(PRICE_RE);
      if (m) return m[0].replace(/\s+/g, ''); // normalize "$ 140.38" -> "$140.38"
    }
    // Header present but no parseable price — most likely the widget copy/markup changed.
    if (DEBUG && !unparsedWarned) {
      unparsedWarned = true;
      warn('Header present but no price parsed from ' + HEADER_SELECTOR +
           ' — widget markup may have changed; override is a no-op until fixed.');
    }
    return '';
  }

  function applyToButton(btn, amount) {
    // 1) Visible amount as a real (announceable) inline node, appended as the LAST child
    //    so it renders after the CTA text. Layout/wrapping is handled in CSS.
    var span = btn.querySelector(':scope > [' + QUOTE_ATTR + ']');
    if (!span) {
      span = document.createElement('span');
      span.setAttribute(QUOTE_ATTR, 'true');
      span.style.whiteSpace = 'normal'; // the span's own text may wrap with the CTA text
      btn.appendChild(span);
    }
    var text = SEPARATOR + amount;
    if (span.textContent !== text) span.textContent = text;

    // 2) Accessible name: append the amount so screen readers announce it. Strip any
    //    previously-appended amount first (stateless — survives node reuse and remount)
    //    so re-runs and re-quotes don't stack.
    if (ANNOUNCE) {
      var cur = btn.getAttribute('aria-label');
      if (cur) {
        var desired = cur.replace(ARIA_STRIP_RE, '') + ARIA_SEP + amount;
        if (cur !== desired) btn.setAttribute('aria-label', desired);
      }
    }
  }

  function sync() {
    var buttons = document.querySelectorAll(BUTTON_SELECTOR);
    if (!buttons.length) return;              // no widget/button present -> nothing to do
    var premium = readPremium();
    if (premium) lastPremium = premium;       // cache only on a valid read
    if (!lastPremium) return;                 // never had a price yet -> wait
    for (var i = 0; i < buttons.length; i++) {
      try {
        applyToButton(buttons[i], lastPremium);
      } catch (e) {
        warn('applyToButton failed (button ' + i + '):', e); // isolate: keep processing the rest
      }
    }
  }

  // Immediate (coalesced) apply. setTimeout, not rAF, so it runs even when the tab is hidden.
  var scheduled = false;
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { scheduled = false; sync(); }, COALESCE_MS);
  }

  // Trailing re-sync burst: catches the async re-quote that lands after a drawer re-render.
  // Armed only on cart activity (see onCartActivity), and reset on each call so it fires
  // relative to the LAST cart event.
  var trailingTimers = [];
  function armTrailing() {
    trailingTimers.forEach(clearTimeout);
    trailingTimers = TRAILING_DELAYS.map(function (d) { return setTimeout(sync, d); });
  }

  function onMutation()     { scheduleSync(); }               // DOM changed: (re)apply, coalesced
  function onCartActivity() { scheduleSync(); armTrailing(); } // cart changed: also catch async re-quote

  function start() {
    try {
      if (!document.body) return;

      var observer = new MutationObserver(onMutation);
      // Body: childList only — detect the button appearing / remounting (cheap).
      observer.observe(document.body, { childList: true, subtree: true });
      // Cart regions: characterData scoped here so in-place price edits are caught without
      // firing on every text change across the whole page.
      CART_ROOT_SELECTORS.forEach(function (sel) {
        var root = document.querySelector(sel);
        if (root) observer.observe(root, { characterData: true, subtree: true });
      });

      CART_EVENTS.forEach(function (evt) {
        document.addEventListener(evt, onCartActivity);
      });

      onCartActivity(); // initial pass (covers the async quote on first render)
      warn('active; debug logging enabled.');
    } catch (e) {
      warn('start failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
// End Route Code